import React from "react";
import { format } from "date-fns";
import { MapPin, Clock, Camera, CheckCircle, AlertCircle, HelpCircle, Check, Brain, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

export default function PhotoGrid({ photos, onPhotoClick, selectMode = false, selectedIds = new Set() }) {
  const getStatusBadge = (status) => {
    switch (status) {
      case "auto_matched":
        return { label: "Matched", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle };
      case "manual_assigned":
        return { label: "Manual", color: "bg-blue-100 text-blue-700", icon: CheckCircle };
      case "out_of_range":
        return { label: "Out of Range", color: "bg-rose-100 text-rose-700", icon: AlertCircle };
      default:
        return { label: "Unassigned", color: "bg-amber-100 text-amber-700", icon: HelpCircle };
    }
  };

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
      {photos.map((photo, index) => {
        const status = getStatusBadge(photo.assignment_status);
        return (
          <motion.div
            key={photo.id}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: index * 0.03 }}
            onClick={() => onPhotoClick?.(photo)}
            className="group cursor-pointer"
          >
            <div className="relative aspect-square rounded-xl overflow-hidden bg-slate-100 border border-slate-200">
              {photo.file_url || photo.thumbnail_url ? (
                <img
                  src={photo.thumbnail_url || photo.file_url}
                  alt="Inspection photo"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Camera className="w-8 h-8 text-slate-300" />
                </div>
              )}
              
              {/* Overlay on hover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              
              {/* Status badge */}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                <Badge className={`${status.color} border-0 text-xs px-2 py-0.5`}>
                  <status.icon className="w-3 h-3 mr-1" />
                  {status.label}
                </Badge>
                {/* Analysis status badge */}
                {photo.analysis_status === "completed" && (
                  <Badge className="bg-violet-100 text-violet-700 border-0 text-xs px-2 py-0.5">
                    <Brain className="w-3 h-3 mr-1" />
                    Analyzed
                  </Badge>
                )}
                {photo.analysis_status === "analyzing" && (
                  <Badge className="bg-violet-100 text-violet-700 border-0 text-xs px-2 py-0.5">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    Analyzing
                  </Badge>
                )}
              </div>

              {/* Selection checkbox */}
              {selectMode && (
                <div className="absolute top-2 right-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${selectedIds.has(photo.id) ? 'bg-blue-600' : 'bg-white/80 border-2 border-slate-400'}`}>
                    {selectedIds.has(photo.id) && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>
              )}

              {/* Bottom info */}
              <div className="absolute bottom-0 left-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity">
                <div className="flex items-center gap-2 text-white text-xs">
                  {photo.gps_lat && photo.gps_lng && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span>GPS</span>
                    </div>
                  )}
                  {photo.taken_at && (
                    <div className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{format(new Date(photo.taken_at), "HH:mm")}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            <div className="mt-2 px-1">
              <p className="text-xs text-slate-500 truncate">
                {photo.inspector_name || "Unknown"}
              </p>
              {photo.taken_at && (
                <p className="text-xs text-slate-400">
                  {format(new Date(photo.taken_at), "MMM d, yyyy")}
                </p>
              )}
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}